import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Field, SectionBand, Status } from '@/components';
import { DriverOutstanding } from '@/types/outstanding';
import { getOutstandingById } from '@/services/api/outstandings';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage, formatRupiah } from '@/utils';
import {
  availableActions,
  driverLabel,
  driverSubLabel,
  formatDate,
  sourceTypeLabel,
} from './outstandingDisplay';
import { useOutstandingActions } from './useOutstandingActions';
import OutstandingActionModals from './components/OutstandingActionModals';

const OutstandingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<DriverOutstanding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    modal,
    openModal,
    closeModal,
    isSubmitting,
    handleSettle,
    handleRelease,
    handleOverride,
  } = useOutstandingActions(setDetail);

  const loadOutstanding = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const row = await getOutstandingById(id);
      if (!row) {
        showErrorToast('Error', 'Outstanding not found');
        navigate('/outstandings');
        return;
      }
      setDetail(row);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load outstanding')
      );
      navigate('/outstandings');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadOutstanding();
  }, [loadOutstanding]);

  if (isLoading) {
    return (
      <div className="bg-white p-6 flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2 text-gray-600">Loading outstanding...</span>
      </div>
    );
  }

  if (!detail) return null;

  const { canSettle, canRelease, canOverride } = availableActions(detail);

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/outstandings')}
            aria-label="Kembali"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <i className="ri-arrow-left-s-line text-[18px]"></i>
          </button>
          <div>
            <h1 className="text-[20px] font-medium text-gray-900">
              Outstanding — {driverLabel(detail)}
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <Status
                value={detail.status}
                type="driverOutstandingStatus"
                size="small"
              />
              <span className="text-[13px] text-gray-500">
                {sourceTypeLabel(detail)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canOverride && (
            <Button
              variant="white"
              size="medium"
              onClick={() => openModal({ type: 'override', row: detail })}
              disabled={isSubmitting}
            >
              Allow New Rental
            </Button>
          )}
          {canSettle && (
            <Button
              variant="primary"
              size="medium"
              onClick={() => openModal({ type: 'settle', row: detail })}
              disabled={isSubmitting}
            >
              <i className="ri-hand-coin-line text-[14px] mr-1"></i>
              Settle
            </Button>
          )}
          {canRelease && (
            <Button
              variant="danger"
              size="medium"
              onClick={() => openModal({ type: 'release', row: detail })}
              disabled={isSubmitting}
            >
              Release
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="w-full lg:flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4">
            <h2 className="text-[18px] font-medium text-gray-900">
              Informasi Outstanding
            </h2>
          </div>

          <SectionBand>Detail Debt</SectionBand>
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <Field label="Mitra">
              <div className="leading-tight">
                <div>{driverLabel(detail)}</div>
                <div className="text-[12px] text-gray-500">
                  {driverSubLabel(detail)} • ID {detail.driverID}
                </div>
              </div>
            </Field>
            <Field label="Source">{sourceTypeLabel(detail)}</Field>
            <Field label="Amount Original">
              {formatRupiah(detail.amountOriginal)}
            </Field>
            <Field label="Sisa Outstanding">
              <span className="font-medium">
                {formatRupiah(detail.amountOutstanding)}
              </span>
            </Field>
            <Field label="Dibuat">
              {formatDate(detail.createdAt)} • {detail.createdBy}
            </Field>
            <Field label="Terakhir Diubah">
              {detail.updatedAt
                ? `${formatDate(detail.updatedAt)} • ${detail.updatedBy ?? '-'}`
                : '-'}
            </Field>
            {detail.sourceRef && (
              <Field label="Rental Terkait">
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/rent-activity/${detail.sourceRef}`)
                  }
                  className="inline-flex items-center gap-1 text-primary underline hover:opacity-80"
                >
                  Lihat Rental
                  <i className="ri-arrow-right-up-line text-[14px]"></i>
                </button>
              </Field>
            )}
          </div>

          <div className="px-5 py-4 border-t border-gray-100">
            <p className="text-[13px] text-gray-500 mb-1">Notes</p>
            <p className="text-[14px] text-gray-900">{detail.notes || '-'}</p>
          </div>

          {detail.newRentalOverrideBy && (
            <>
              <SectionBand>Override Aktif</SectionBand>
              <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <Field label="Di-override oleh">
                  {detail.newRentalOverrideBy}
                </Field>
                <Field label="Alasan">
                  {detail.newRentalOverrideReason || '-'}
                </Field>
              </div>
            </>
          )}

          {detail.releasedAt && (
            <>
              <SectionBand>Release Info</SectionBand>
              <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <Field label="Di-release oleh">
                  {detail.releasedBy || '-'}
                </Field>
                <Field label="Tanggal Release">
                  {formatDate(detail.releasedAt)}
                </Field>
                <Field label="Alasan">
                  <span className="sm:col-span-2">
                    {detail.releaseReason || '-'}
                  </span>
                </Field>
              </div>
            </>
          )}
        </div>
      </div>

      <OutstandingActionModals
        modal={modal}
        onClose={closeModal}
        isSubmitting={isSubmitting}
        onSettle={handleSettle}
        onRelease={handleRelease}
        onOverride={handleOverride}
      />
    </div>
  );
};

export default OutstandingDetailPage;
